import { H1, H2, P, UL, LI, Code, Note, Divider, StepList } from './primitives';

export function SectionOverview() {
  return (
    <>
      <H1>Overview</H1>
      <P>
        <strong className="text-ink">aiai·loop</strong> is a self-hostable
        platform that runs specialised AI agents inside isolated Docker
        containers to automate software engineering tasks. You write a
        plain-English brief, attach it to a GitHub repository, and the platform
        spins up a pipeline of role-specialised agents that clone the repo,
        implement the change in a test-gated loop, push a branch, and open a
        pull request. Pull requests that pass the test gate merge
        themselves — nothing waits for a human approval.
      </P>
      <P>
        Beyond one-off briefs, the platform can run{' '}
        <strong className="text-ink">scheduled loops</strong> — agent teammates
        that clock in on a cron schedule with a standing mission — and{' '}
        <strong className="text-ink">prototypes</strong> — throwaway apps built
        from a one-paragraph idea and served on a live preview URL.
      </P>

      <H2>The loop-engineering concept</H2>
      <P>
        The core idea is the <em>test-gated loop</em>. After an agent finishes
        a stage, a <strong>gate command</strong> (typically the project's test
        suite) runs. If the gate fails, the loop restarts the stage with the
        failure output fed back in — up to the configured{' '}
        <strong>max loops</strong> budget. The agent keeps converging until the
        tests pass, the budget runs out, or you step in.
      </P>

      <H2>Finding your way around</H2>
      <UL>
        <LI>
          <strong className="text-ink">Mission control</strong> — the home
          page: a live scene of every project and its working agents, a{' '}
          <em>Needs you</em> list (issues that gave up, missing GitHub tokens,
          failed deploys), a live feed, and a replay of what happened while
          you were away.
        </LI>
        <LI>
          <strong className="text-ink">Activity</strong> — the same work as
          plain lists: running agents, recently updated issues, and scheduled
          loops.
        </LI>
        <LI>
          <strong className="text-ink">Projects</strong> — your linked GitHub
          repositories and their issues.
        </LI>
        <LI>
          <strong className="text-ink">Prototypes</strong> — throwaway apps
          with live preview URLs.
        </LI>
        <LI>
          The <strong className="text-ink">account menu</strong> (top right)
          holds Metrics, Settings, and these Docs. The{' '}
          <strong className="text-ink">jump palette</strong> in the header
          gets you anywhere by keyboard.
        </LI>
      </UL>

      <H2>Key concepts at a glance</H2>
      <UL>
        <LI>
          <strong className="text-ink">Project</strong> — a GitHub repository
          paired with defaults such as the base branch.
        </LI>
        <LI>
          <strong className="text-ink">Issue</strong> — a markdown brief (the
          task description) attached to a project, with a pipeline and a loop
          budget.
        </LI>
        <LI>
          <strong className="text-ink">Run</strong> — one execution of an
          issue through the agent pipeline; produces logs, stage results, a
          branch, and usually a pull request.
        </LI>
        <LI>
          <strong className="text-ink">Stage</strong> — a single agent role's
          turn within a run (e.g. the Worker stage).
        </LI>
        <LI>
          <strong className="text-ink">Gate</strong> — a shell command that
          must exit 0 for the pipeline to advance; failure triggers an
          automatic retry, and an exhausted budget pauses for your approval.
        </LI>
        <LI>
          <strong className="text-ink">Loop</strong> — a scheduled agent with
          a standing mission that fires on a cron expression.
        </LI>
        <LI>
          <strong className="text-ink">Prototype</strong> — a generated app
          repo plus a deployed preview container with a live URL.
        </LI>
      </UL>

      <Note>
        New here? Continue with <em>Getting Started</em>, then skim the three{' '}
        <em>Architecture</em> pages — they explain what actually happens when
        you hand work to the agents.
      </Note>
    </>
  );
}

export function SectionGettingStarted() {
  return (
    <>
      <H1>Getting Started</H1>
      <P>
        The first time you open aiai·loop you are redirected to the setup
        wizard. Complete it once to create the first account and configure the
        essential credentials.
      </P>

      <H2>First-run setup wizard</H2>
      <StepList
        steps={[
          {
            n: 1,
            title: 'Create your account',
            body: (
              <p>
                Enter an email address and a strong password. This creates the
                first user account and a personal workspace that you own. Invite
                more people to it from{' '}
                <strong className="text-ink">Workspace settings → Members</strong>{' '}
                — as members (who see only the projects you grant them) or as
                co-owners with full control; an owner can also make an existing
                member an owner there. New customers and their workspaces are set up by the platform
                operator on the separate ops console, not in this app.
              </p>
            ),
          },
          {
            n: 2,
            title: 'Enrol two-factor authentication',
            body: (
              <p>
                2FA is required by default (<Code>AUTH_REQUIRE_2FA=true</Code>).
                After your first login you are taken to the enrolment screen —
                scan the QR code with any TOTP app and store the recovery
                codes. See <em>Security (2FA)</em>.
              </p>
            ),
          },
          {
            n: 3,
            title: 'Add a Claude subscription token',
            body: (
              <p>
                Paste your <Code>CLAUDE_CODE_OAUTH_TOKEN</Code> from{' '}
                <Code>claude setup-token</Code>. You can skip this during setup
                and add it later in <strong>Settings</strong> — but no agent
                can run without it.
              </p>
            ),
          },
          {
            n: 4,
            title: 'Add a GitHub personal access token',
            body: (
              <p>
                A classic PAT with the <Code>repo</Code> and{' '}
                <Code>delete_repo</Code> scopes (fine-grained tokens can't
                reliably create or delete repositories). Required to clone, push
                branches, open PRs, use the GitHub Issues integration, and — for
                prototypes — create and delete their own repos. Add it later in{' '}
                <strong>Settings</strong> if you prefer.
              </p>
            ),
          },
        ]}
      />

      <Divider />

      <H2>Your first run</H2>
      <StepList
        steps={[
          {
            n: 1,
            title: 'Create a project',
            body: (
              <p>
                <strong>Projects → New project</strong>, pick a repository from
                the dropdown (populated via your GitHub token).
              </p>
            ),
          },
          {
            n: 2,
            title: 'Write an issue',
            body: (
              <p>
                <strong>New issue</strong> on the project page. Describe the
                task in markdown — context, goal, acceptance criteria.
              </p>
            ),
          },
          {
            n: 3,
            title: 'Save & run',
            body: (
              <p>
                Click <strong>Save &amp; run</strong>. A run starts, and you can
                watch the agent think, edit, and test live on the run page.
              </p>
            ),
          },
          {
            n: 4,
            title: 'The pull request merges',
            body: (
              <p>
                When the run succeeds a PR is opened and merges itself once
                GitHub reports it mergeable; its risk level is recorded on the
                PR — see <em>Auto-merge &amp; Risk</em>.
              </p>
            ),
          },
        ]}
      />

      <Note>
        <strong>Subscription vs API key:</strong> aiai·loop authenticates with
        an OAuth subscription token, not a metered API key. This keeps costs
        predictable and avoids per-token billing.
      </Note>
    </>
  );
}
